import React, { useMemo } from 'react';

import { Select } from 'antd';
import type { GUnit } from 'interfaces/IGlobalCatalogue';
import type { IUnit } from 'interfaces/IInput';
import { AREA_UNIT_FILTER, filterGUnitsByFilter } from 'utils/GUnitFilters';

interface AreaUnitSelectProps {
  value: IUnit | null;
  gUnits: GUnit[];
  ariaLabel: string;
  onChange: (unit: IUnit) => void;
}

/**
 * Area unit picker for a Paddocks grid row (OP-2481). Saves as soon as a
 * unit is picked, like the grid's other fields.
 */
const AreaUnitSelect: React.FC<AreaUnitSelectProps> = ({
  value,
  gUnits,
  ariaLabel,
  onChange,
}) => {
  const areaUnits = useMemo(
    () => filterGUnitsByFilter(gUnits, AREA_UNIT_FILTER),
    [gUnits]
  );

  // Match on globalId first, then the code — older records can carry an
  // empty globalId.
  const selected = areaUnits.find(
    (unit) => unit.globalId === value?.globalId || unit.code === value?.unit
  );

  return (
    <div className="paddocks-grid-field">
      <Select
        aria-label={ariaLabel}
        className="w-full"
        value={selected?.globalId}
        // Shows the stored code while the catalogue loads or can't match it.
        placeholder={value?.unit}
        options={areaUnits.map((unit) => ({
          value: unit.globalId,
          label: unit.code,
        }))}
        onChange={(globalId: string) => {
          const unit = areaUnits.find((item) => item.globalId === globalId);
          if (unit && unit.globalId !== selected?.globalId) {
            onChange({ unit: unit.code, globalId: unit.globalId });
          }
        }}
      />
    </div>
  );
};

export default AreaUnitSelect;
